import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api, proxyImg } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import {
  ArticleCard, ToolCard, ComparisonCard, ModelCard, SectionHeader, Loader, Reveal,
  Overline, Meta, LazyImage, ArrowUpRight,
} from "@/components/shared";
import { AdSlot, DealCard } from "@/components/Ads";
import { Clock, TrendingUp } from "lucide-react";

export default function Home() {
  const [data, setData] = useState(null);
  useSEO({ title: "KLYROO — AI. Gaming. Technology. Explained.", description: "Deep AI tool reviews, model comparisons, gaming and hardware coverage from a modern technology intelligence platform." });
  useEffect(() => { api.home().then(setData); }, []);
  if (!data) return <Loader />;
  const { featured, trending, ai, gaming, tech, reviews, guides, comparisons, tools, models, deals, counts } = data;

  // "Latest on KLYROO" — admin-curated list first, else the backend's category-diverse
  // recency mix (data.latest). Never AI-biased and not redirected to the AI category.
  const latest = ((data.curated_latest && data.curated_latest.length ? data.curated_latest : data.latest) || []).slice(0, 8);

  // Hero rotates through the featured story + top trending posts.
  const heroStories = [];
  const hs = new Set();
  for (const s of [featured, ...trending]) {
    if (s && !hs.has(s.slug)) { hs.add(s.slug); heroStories.push(s); }
    if (heroStories.length >= 5) break;
  }

  const heroPills = [["Explore AI Tools", "/tools", true], ["Compare AI", "/comparisons"], ["AI Models", "/ai-models"], ["Today's Deals", "/deals"], ["Gaming", "/gaming"]];
  const stats = [["AI Tools", counts.tools, "/tools"], ["AI Models", counts.models, "/ai-models"], ["Comparisons", counts.comparisons, "/comparisons"], ["Articles", counts.articles, "/reviews"]];

  return (
    <div className="klyroo-radial">
      {/* HERO */}
      <section className="klyroo-grid border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 lg:pt-14 pb-9">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
            {/* Left: value proposition + CTAs + stats */}
            <div className="lg:col-span-5 flex flex-col fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-klyroo-cyan/30 bg-klyroo-cyan/5 mb-5 w-fit">
                <span className="w-1.5 h-1.5 rounded-full bg-klyroo-cyan animate-pulse" />
                <span className="overline">Technology Intelligence Platform</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.02]">
                AI. Gaming. Technology.<br /><span className="cyan-text">Explained.</span>
              </h1>
              <p className="text-slate-300 mt-5 text-base sm:text-lg leading-relaxed max-w-xl">
                Your intelligence layer for the tech world — <strong className="text-white">{counts.tools}+ AI tools</strong> reviewed,
                {" "}<strong className="text-white">{counts.models} models</strong> tracked and <strong className="text-white">{counts.comparisons}+ deep comparisons</strong> across AI, gaming and hardware.
              </p>
              <div className="flex flex-wrap gap-2.5 mt-6">
                {heroPills.map(([label, to, primary]) => (
                  <Link key={to} to={to} data-testid={`hero-pill-${to.slice(1)}`}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${primary ? "bg-klyroo-cyan text-black hover:bg-white" : "border border-white/15 text-slate-200 hover:border-klyroo-cyan/50 hover:text-white"}`}>
                    {label}
                  </Link>
                ))}
              </div>
              {/* stats — clickable, close to the hero content */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-3 mt-8 lg:mt-auto lg:pt-8">
                {stats.map(([l, n, to]) => (
                  <Link key={l} to={to} data-testid={`hero-stat-${to.slice(1)}`} className="group card-surface rounded-2xl p-4 hover-raise">
                    <div className="font-display text-2xl sm:text-3xl font-extrabold text-white group-hover:text-klyroo-cyan transition-colors">{n}+</div>
                    <div className="overline mt-1 flex items-center gap-1">{l} <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right: featured story (auto-rotates through top trending) */}
            <div className="lg:col-span-7 fade-up" style={{ animationDelay: ".08s" }}>
              <HeroFeatured stories={heroStories} />
            </div>
          </div>

          {/* Trending band */}
          {trending && trending.length > 0 && (
            <div className="mt-10 fade-up" style={{ animationDelay: ".16s" }}>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-klyroo-cyan" />
                <span className="font-display font-bold text-white tracking-tight">Trending Now</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {trending.slice(0, 5).map((a, i) => (
                  <Link key={a.slug} to={`/article/${a.slug}`} data-testid={`trending-${a.slug}`}
                    className="group flex gap-3 card-surface rounded-2xl p-4 hover-raise">
                    <span className="font-display font-extrabold text-lg text-klyroo-cyan/50 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-200 font-medium leading-snug group-hover:text-klyroo-cyan transition-colors line-clamp-2">{a.title}</p>
                      <span className="text-[11px] font-mono text-slate-500">{a.category}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Sponsored — integrated into the flow */}
        <div className="pt-8"><AdSlot placement="home-top" /></div>

        {/* LATEST ON KLYROO */}
        {latest.length > 0 && (
          <Section overline="Fresh from the newsroom" title="Latest on KLYROO" to="/reviews" label="Browse articles">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 reveal-stagger">
              {latest.map((a) => <ArticleCard key={a.slug} a={a} />)}
            </div>
          </Section>
        )}
        {/* AI TOOLS */}
        <Section overline="Directory · 100+ Tools" title="AI Tools" to="/tools" label="Explore all tools">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 reveal-stagger">
            {tools.slice(0, 12).map((t) => <ToolCard key={t.slug} t={t} />)}
          </div>
        </Section>

        {/* COMPARISONS */}
        <Section overline="Head to head" title="Latest Comparisons" to="/comparisons" label="All comparisons">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 reveal-stagger">
            {comparisons.slice(0, 8).map((c) => <ComparisonCard key={c.slug} c={c} />)}
          </div>
        </Section>

        {/* DEALS */}
        {deals && deals.length > 0 && (
          <Section overline="Save money" title="Today's Top Deals" to="/deals" label="All deals">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 reveal-stagger">
              {deals.slice(0, 4).map((d) => <DealCard key={d.slug} d={d} />)}
            </div>
          </Section>
        )}

        <div className="pb-4"><AdSlot placement="home-mid" /></div>

        {/* Mobile-only featured ad */}
        <div className="md:hidden pb-8"><AdSlot placement="mobile-feature" className="max-w-xs mx-auto" /></div>

        {/* AI */}
        <Section overline="Artificial Intelligence" title="AI" to="/ai" label="More AI">
          <FeatureGrid items={ai} />
        </Section>

        {/* MODELS */}
        <Section overline="Model Database" title="AI Models" to="/ai-models" label="All models">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 reveal-stagger">
            {models.slice(0, 8).map((m) => <ModelCard key={m.slug} m={m} />)}
          </div>
        </Section>

        {/* GAMING */}
        <Section overline="Play" title="Gaming" to="/gaming" label="More gaming">
          <FeatureGrid items={gaming} />
        </Section>

        {/* TECH */}
        <Section overline="Hardware & Devices" title="Technology" to="/tech" label="More tech">
          <FeatureGrid items={tech} />
        </Section>

        {/* REVIEWS + GUIDES */}
        <Reveal className="grid lg:grid-cols-2 gap-10 py-12">
          <div>
            <SectionHeader overline="Verdicts" title="Reviews" action={<Link to="/reviews" className="text-sm text-klyroo-cyan font-mono flex items-center gap-1">All <ArrowUpRight size={14} /></Link>} />
            <div className="space-y-4 reveal-stagger">{reviews.slice(0, 4).map((a) => <ListRow key={a.slug} a={a} />)}</div>
          </div>
          <div>
            <SectionHeader overline="How-to" title="Guides" action={<Link to="/guides" className="text-sm text-klyroo-cyan font-mono flex items-center gap-1">All <ArrowUpRight size={14} /></Link>} />
            <div className="space-y-4 reveal-stagger">{guides.slice(0, 4).map((a) => <ListRow key={a.slug} a={a} />)}</div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

function HeroFeatured({ stories }) {
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  useEffect(() => {
    if (!stories || stories.length <= 1) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => { if (!paused.current) setIdx((i) => (i + 1) % stories.length); }, 6000);
    return () => clearInterval(t);
  }, [stories]);

  if (!stories || !stories.length) return null;
  const f = stories[Math.min(idx, stories.length - 1)];

  return (
    <div className="relative h-full min-h-[360px]"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}>
      <Link key={f.slug} to={`/article/${f.slug}`} data-testid="hero-featured"
        className="hero-swap group absolute inset-0 flex flex-col justify-end overflow-hidden rounded-3xl card-surface hover-raise">
        <img src={proxyImg(f.image)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-70" />
        <img src={proxyImg(f.image)} alt={f.image_alt || f.title} className="absolute inset-0 h-full w-full object-contain" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
        <span className="absolute top-4 left-4 z-10 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-klyroo-cyan text-black font-bold">Featured Story</span>
        <div className="relative p-6 sm:p-8 lg:p-9 pr-16 sm:pr-20">
          <div className="flex items-center gap-3 mb-3">
            <span className="overline text-klyroo-cyan">{f.category}{f.content_type ? ` · ${f.content_type}` : ""}</span>
            <Meta><Clock size={12} /> {f.reading_time} min</Meta>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-[1.08] tracking-tight group-hover:text-klyroo-cyan transition-colors line-clamp-3">{f.title}</h2>
          <p className="text-slate-300 mt-3 max-w-2xl text-base sm:text-lg line-clamp-2">{f.subtitle}</p>
        </div>
      </Link>
      {stories.length > 1 && (
        <div className="absolute bottom-5 right-5 z-20 flex gap-1.5" data-testid="hero-dots">
          {stories.map((s, i) => (
            <button key={s.slug} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`} aria-label={`Show featured story ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-klyroo-cyan" : "w-1.5 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ overline, title, to, label, children }) {
  return (
    <section className="py-12">
      <Reveal>
        <SectionHeader overline={overline} title={title}
          action={<Link to={to} className="text-sm text-klyroo-cyan font-mono flex items-center gap-1 hover:gap-2 transition-all">{label} <ArrowUpRight size={14} /></Link>} />
        {children}
      </Reveal>
    </section>
  );
}

function FeatureGrid({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-3 reveal-stagger">
      <div className="lg:row-span-2"><ArticleCard a={items[0]} size="lg" /></div>
      {items.slice(1, 5).map((a) => <ArticleCard key={a.slug} a={a} />)}
    </div>
  );
}

function ListRow({ a }) {
  return (
    <Link to={`/article/${a.slug}`} className="group flex gap-4 card-surface rounded-2xl p-3 hover-raise">
      <div className="w-28 sm:w-36 shrink-0 rounded-xl overflow-hidden"><LazyImage src={a.image} alt={a.image_alt} ratio="4/3" /></div>
      <div className="py-1">
        <span className="overline text-klyroo-cyan">{a.subcategory || a.category}</span>
        <h3 className="font-display font-bold text-white mt-1 leading-snug group-hover:text-klyroo-cyan transition-colors line-clamp-2">{a.title}</h3>
        <Meta><Clock size={11} /> {a.reading_time} min read</Meta>
      </div>
    </Link>
  );
}
